<template>
  <ToolShell
    tool-name="Blank invoice template"
    h1="Free blank invoice template to print or save as PDF."
    lede="A clean, printable blank invoice you fill in by hand or on screen. Add your business name if you like, choose the paper size and number of lines, then print it or save it as a PDF. Free, no signup, and nothing you type leaves your browser."
    :trust="['No signup', 'Print or PDF', 'Letter or A4']"
    form-title="Template options"
    :upsell-points="[
      'Invoices numbered and dated for you',
      'Clients pay online by card or bank transfer',
      'Reminders sent when an invoice is overdue',
    ]"
    free-label="The blank template"
    :free-bullets="[
      'A blank form you fill in and send yourself',
      'Totals added up by hand',
      'Payments and follow-ups tracked somewhere else',
    ]"
    slug="/tools/blank-invoice-template"
    :meta-description="metaDescription"
    :doc-brand="form.business || 'Your business name'"
    doc-subtitle="Blank invoice"
    doc-kind="INVOICE"
    stamp="Blank"
  >
    <template #form>
      <label class="ta-field">
        <span>Your business name (optional)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 14V4.5L8 2l6 2.5V14M5.5 14v-4h5v4M2 14h12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input v-model.trim="form.business" type="text" maxlength="80" placeholder="Leave empty for a fully blank form" />
        </div>
      </label>

      <div class="ta-field-row">
        <div class="ta-field">
          <span id="blank-invoice-paper-label">Paper size</span>
          <ToolDropdown v-model="form.paper" :options="paperOptions" aria-label="Paper size" labelledby="blank-invoice-paper-label" />
        </div>
        <div class="ta-field">
          <span id="blank-invoice-lines-label">Line items</span>
          <ToolDropdown v-model="form.lines" :options="lineOptions" aria-label="Line items" labelledby="blank-invoice-lines-label" />
        </div>
      </div>

      <label class="ta-field">
        <span>Currency symbol (optional)</span>
        <div class="ta-input ta-input-sm">
          <input v-model.trim="form.currency" type="text" maxlength="3" placeholder="$" />
        </div>
      </label>

      <label class="bi-check"><input v-model="form.tax" type="checkbox" /> Include a tax line</label>
      <label class="bi-check"><input v-model="form.payment" type="checkbox" /> Include a "How to pay" box</label>

      <button type="button" class="ta-download" @click="printPdf">
        Print or save as PDF
        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6V2h8v4M4 12H2.5V6h11v6H12M4 9.5h8V14H4z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>
      </button>
      <button type="button" class="ta-download bi-secondary" @click="downloadWord">Download for Word</button>

      <p class="ta-note">
        In the print dialog, choose "Save as PDF" to keep a fillable copy. Want the totals worked out for you? Use the
        <NuxtLink :to="localePath('/tools/invoice-generator')">free invoice generator</NuxtLink>
        or the <NuxtLink :to="localePath('/tools/invoice-template')">Excel and Sheets invoice template</NuxtLink>.
      </p>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Invoice #</span><strong>________</strong></div>
        <div><span>Date</span><strong>________</strong></div>
        <div><span>Due</span><strong>________</strong></div>
      </div>
      <div class="bi-parties">
        <div><span>From</span><i /><i /></div>
        <div><span>Bill to</span><i /><i /></div>
      </div>
      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table bi-table">
          <thead>
            <tr><th>Description</th><th>Qty</th><th>Rate</th><th>Amount</th></tr>
          </thead>
          <tbody>
            <tr v-for="n in previewLines" :key="n"><td>&nbsp;</td><td /><td /><td /></tr>
          </tbody>
        </table>
      </div>
      <div class="bi-totals">
        <div><span>Subtotal</span><i /></div>
        <div v-if="form.tax"><span>Tax</span><i /></div>
        <div class="bi-total"><span>Total due {{ form.currency }}</span><i /></div>
      </div>
      <p v-if="form.payment" class="ta-doc-note">How to pay: ______________________</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Set it up</h2><p>Add your business name or leave it blank, then pick Letter or A4 and how many lines you need.</p></div></div>
      <div><span>02</span><div><h2>Print or save</h2><p>Print a stack to fill in by hand, or save a PDF you can fill in on screen.</p></div></div>
      <div><span>03</span><div><h2>Fill in and send</h2><p>Add the invoice number, dates, client, line items and total, then send it with your payment details.</p></div></div>
    </template>

    <section class="tool-faq">
      <div class="tool-faq-inner">
        <h2>Blank invoice template FAQ</h2>
        <div class="tool-faq-list">
          <details v-for="item in faq" :key="item.q" class="tool-faq-item">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>
    </section>

    <RelatedPages path="/tools/blank-invoice-template" title="Blank invoice template" />

    <Cta03 />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolShell from "~/components/tools/ToolShell.vue";

const { localePath } = useWorkspaceLocale();

const form = reactive({
  business: "",
  paper: "letter",
  lines: "10",
  currency: "",
  tax: true,
  payment: true,
});

const paperOptions = [
  { value: "letter", label: "US Letter", hint: "8.5 × 11 in" },
  { value: "a4", label: "A4", hint: "210 × 297 mm" },
];
const lineOptions = ["6", "10", "14"].map((n) => ({ value: n, label: `${n} lines` }));
const previewLines = computed(() => Math.min(Number(form.lines), 6));

const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

const invoiceHtml = () => {
  const cur = escapeHtml(form.currency);
  const rows = Array.from({ length: Number(form.lines) }, () => "<tr><td>&nbsp;</td><td></td><td></td><td></td></tr>").join("");
  const brand = form.business ? `<div class="brand">${escapeHtml(form.business)}</div>` : '<div class="blank wide"></div>';
  return `<!doctype html><html><head><meta charset="utf-8"><title>Blank invoice</title><style>
@page { size: ${form.paper === "a4" ? "A4" : "letter"}; margin: 16mm; }
body { font-family: Helvetica, Arial, sans-serif; color: #111; font-size: 11pt; margin: 0; }
h1 { font-size: 26pt; letter-spacing: 0.08em; margin: 0; }
.top { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; margin-bottom: 18px; }
.brand { font-size: 15pt; font-weight: bold; }
.meta td { padding: 3px 0 3px 10px; } .meta td:first-child { color: #555; padding-left: 0; }
.blank { border-bottom: 1px solid #999; height: 20px; min-width: 120px; } .wide { width: 260px; }
.parties { display: flex; gap: 32px; margin: 10px 0 18px; } .parties > div { flex: 1; }
.label { font-size: 9pt; text-transform: uppercase; color: #555; letter-spacing: 0.06em; }
table.items { width: 100%; border-collapse: collapse; }
table.items th { background: #f1f1f1; text-align: left; font-size: 9pt; text-transform: uppercase; padding: 6px; border: 1px solid #bbb; }
table.items td { border: 1px solid #bbb; height: 24px; padding: 4px 6px; }
table.items th:nth-child(1) { width: 55%; }
.totals { margin-left: auto; width: 45%; margin-top: 10px; }
.totals div { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; padding: 4px 0; }
.totals .blank { flex: 1; }
.total { font-weight: bold; font-size: 12pt; }
.box { border: 1px solid #bbb; padding: 8px 10px; margin-top: 18px; min-height: 60px; }
.foot { margin-top: 18px; font-size: 9pt; color: #555; }
</style></head><body>
<div class="top"><div>${brand}<div class="blank wide"></div><div class="blank wide"></div></div>
<div><h1>INVOICE</h1><table class="meta"><tr><td>Invoice #</td><td><div class="blank"></div></td></tr><tr><td>Date</td><td><div class="blank"></div></td></tr><tr><td>Due date</td><td><div class="blank"></div></td></tr></table></div></div>
<div class="parties"><div><div class="label">Bill to</div><div class="blank"></div><div class="blank"></div><div class="blank"></div></div>
<div><div class="label">Project or PO</div><div class="blank"></div><div class="blank"></div></div></div>
<table class="items"><thead><tr><th>Description</th><th>Qty / hours</th><th>Rate</th><th>Amount</th></tr></thead><tbody>${rows}</tbody></table>
<div class="totals"><div><span>Subtotal ${cur}</span><div class="blank"></div></div>${form.tax ? `<div><span>Tax ____ %</span><div class="blank"></div></div>` : ""}<div class="total"><span>Total due ${cur}</span><div class="blank"></div></div></div>
${form.payment ? '<div class="box"><div class="label">How to pay</div></div>' : ""}
<div class="foot">Payment terms: ______________________ &nbsp; Thank you for your business.</div>
</body></html>`;
};

const printPdf = () => {
  const win = window.open("", "_blank");
  if (!win) return;
  win.document.open();
  win.document.write(invoiceHtml());
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 250);
};

const downloadWord = () => {
  const blob = new Blob(["﻿", invoiceHtml()], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `blank-invoice-${form.paper}.doc`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const faq = [
  {
    q: "What is a blank invoice template?",
    a: "It is an invoice layout with every field left empty: your details, the client, invoice number, dates, line items, totals and payment terms. You fill it in by hand or on screen each time you bill. It suits freelancers and small service businesses that send a few invoices a month and do not need software yet.",
  },
  {
    q: "What do I need to fill in on a blank invoice?",
    a: "Your business name and contact details, the client's name and billing address, a unique invoice number, the issue date and due date, a line for each service with quantity, rate and amount, the subtotal, any tax, the total due, and how the client can pay. Check your local rules: some countries require extra details such as a tax or VAT number.",
  },
  {
    q: "How do I save the blank invoice as a PDF?",
    a: "Click Print or save as PDF, then choose Save as PDF (or Microsoft Print to PDF on Windows) as the printer in the dialog. You can print paper copies the same way, or download the Word version and type into it.",
  },
  {
    q: "How should I number invoices?",
    a: "Use a sequence that never repeats, such as INV-001, INV-002, or a year prefix like 2026-014. Keep a simple log of the numbers you have used so you can match payments to invoices and answer client questions quickly.",
  },
  {
    q: "When is a blank template not enough?",
    a: "When you send invoices every week, bill several clients, or keep chasing late payments. At that point invoicing software saves time: it numbers invoices, adds up totals, takes card or bank payments and sends reminders. Workspace369 does this from the same client record as your proposals and projects.",
  },
  {
    q: "Is anything I type stored?",
    a: "No. The template is built in your browser, and nothing you type is sent to Workspace369 or saved after you close the page.",
  },
];

const metaTitle = "Free Blank Invoice Template (PDF or Word) | Workspace369";
const metaDescription =
  "Free blank invoice template: print it or save it as a PDF, in US Letter or A4, with optional tax and payment boxes. Fill it in by hand or on screen. No signup.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
  script: [
    {
      key: "tool-faq-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
.bi-check {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.35rem 0;
  color: #374151;
  font-size: 0.88rem;
}
button.ta-download.bi-secondary {
  margin-top: 0.6rem;
  background: #fff;
  color: #111827;
  border: 1px solid #d1d5db;
  box-shadow: none;
}
button.ta-download.bi-secondary:hover {
  background: #f3f4f6;
  box-shadow: none;
}
.bi-parties {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin: 1.1rem 1.7rem 0;
}
.bi-parties div,
.bi-totals div {
  display: grid;
  gap: 0.35rem;
}
.bi-parties span,
.bi-totals span {
  color: #6b7280;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.bi-parties i,
.bi-totals i {
  display: block;
  height: 1rem;
  border-bottom: 1px solid #d1d5db;
}
.bi-table td {
  height: 1.6rem;
}
.bi-totals {
  display: grid;
  gap: 0.4rem;
  width: 55%;
  margin: 1.1rem 1.7rem 0 auto;
}
.bi-totals div {
  grid-template-columns: auto 1fr;
  align-items: end;
}
.bi-total span {
  color: #111827;
  font-weight: 700;
}

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .bi-check {
  color: #cbd5e1;
}

.dark button.ta-download.bi-secondary {
  background: #0f172a;
  color: #f1f5f9;
  border: 1px solid #243247;
}

.dark button.ta-download.bi-secondary:hover {
  background: #020617;
}

.dark .bi-parties span,
.dark .bi-totals span {
  color: #cbd5e1;
}

.dark .bi-parties i,
.dark .bi-totals i {
  border-bottom: 1px solid #243247;
}

.dark .bi-total span {
  color: #f1f5f9;
}


</style>
